import React from "react";
import PropTypes from "prop-types";
import { TvDiv } from "@trustvid/design-system";
import LoginFeatureHero from "./LoginFeatureHero";
import LoginFeatureStats from "./LoginFeatureStats";
import LoginFeatureTestimonial from "./LoginFeatureTestimonial";
import LoginFeatureGrid from "./LoginFeatureGrid";
import LoginMarketingUseCases from "./LoginMarketingUseCases";
import { LOGIN_FEATURE_SECTIONS } from "./loginPageContent";
import { isMarketingDemosPublicEnabled } from "../../../utils/marketing-demos-visibility";

const trustvidLogo = require("../../../assets/images/trustvid-logo.png");

const LoginMarketingBrand = ({ compact = false }) => (
  <TvDiv
    className={`tv-login-page__panel-brand d-flex align-items-center gap-2${compact ? "" : " gap-lg-3"}`}
  >
    <img src={trustvidLogo} alt="Trustvid" className="tv-login-page__panel-logo" />
    <span className="tv-login-page__panel-name">Trustvid</span>
  </TvDiv>
);

LoginMarketingBrand.propTypes = {
  compact: PropTypes.bool,
};

/**
 * Marketing content for auth pages.
 * @param {"full"|"compact-hero"|"compact-cards"} variant
 */
const LoginMarketing = ({
  variant = "full",
  pageHeading,
  sections = LOGIN_FEATURE_SECTIONS,
  className = "",
  testId,
}) => {
  const isCompactHero = variant === "compact-hero";
  const isCompactCards = variant === "compact-cards";
  const isFull = variant === "full";

  const rootTestId =
    testId ?? (isFull ? "login-feature-panel" : isCompactHero ? "login-feature-compact" : "login-feature-compact-cards");

  if (isCompactHero) {
    return (
      <TvDiv
        className={`tv-login-page__marketing-compact-hero d-flex flex-column ${className}`.trim()}
        data-testid={rootTestId}
      >
        <LoginMarketingBrand compact />
        <LoginFeatureHero pageHeading={pageHeading} compact />
        <LoginFeatureStats compact />
      </TvDiv>
    );
  }

  if (isCompactCards) {
    return (
      <TvDiv
        className={`tv-login-page__marketing-compact-cards ${className}`.trim()}
        data-testid={rootTestId}
      >
        <LoginFeatureGrid sections={sections} columns={2} compact />
        {isMarketingDemosPublicEnabled() ? (
          <TvDiv className="mt-3">
            <LoginMarketingUseCases compact />
          </TvDiv>
        ) : null}
      </TvDiv>
    );
  }

  return (
    <TvDiv
      className={`tv-login-page__features d-flex flex-column ${className}`.trim()}
      data-testid={rootTestId}
    >
      <LoginMarketingBrand />
      <TvDiv className="tv-login-page__hero-block d-flex flex-column">
        <LoginFeatureHero pageHeading={pageHeading} />
        <LoginFeatureStats />
      </TvDiv>
      {isMarketingDemosPublicEnabled() ? <LoginMarketingUseCases /> : null}
      <LoginFeatureGrid sections={sections} columns={3} />
      <LoginFeatureTestimonial />
    </TvDiv>
  );
};

LoginMarketing.propTypes = {
  variant: PropTypes.oneOf(["full", "compact-hero", "compact-cards"]),
  pageHeading: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          icon: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ),
  className: PropTypes.string,
  testId: PropTypes.string,
};

export default LoginMarketing;
